import moment from 'moment-jalaali';

/** تبدیل اعداد فارسی/عربی به انگلیسی */
export function toLatinDigits(value) {
  if (value == null) return '';
  const persian = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
  const arabic = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
  let result = String(value);
  for (let i = 0; i < 10; i += 1) {
    result = result.replace(new RegExp(persian[i], 'g'), String(i));
    result = result.replace(new RegExp(arabic[i], 'g'), String(i));
  }
  return result;
}

/**
 * تبدیل تاریخ انتخابی فرم (جلالی یا میلادی) به YYYY-MM-DD میلادی برای API
 */
export function toApiDate(value) {
  if (value == null || value === '') return null;

  if (typeof value === 'object') {
    if (typeof value.format === 'function') {
      try {
        // DateObject از react-multi-date-picker — اگر تقویم جلالی باشد
        const jalali = toLatinDigits(value.format('YYYY/MM/DD'));
        const m = moment(jalali, 'jYYYY/jMM/jDD');
        if (m.isValid()) return m.format('YYYY-MM-DD');
      } catch {
        /* fall through */
      }
    }
    if (value.jYear != null && value.jMonth != null && value.jDay != null) {
      const jYear = typeof value.jYear === 'function' ? value.jYear() : value.jYear;
      const jMonth = typeof value.jMonth === 'function' ? value.jMonth() + 1 : value.jMonth + 1;
      const jDay = typeof value.jDay === 'function' ? value.jDay() : value.jDay;
      const m = moment(
        `${jYear}/${String(jMonth).padStart(2, '0')}/${String(jDay).padStart(2, '0')}`,
        'jYYYY/jMM/jDD',
      );
      if (m.isValid()) return m.format('YYYY-MM-DD');
    }
  }

  const raw = toLatinDigits(value).replace(/-/g, '/').trim();
  if (!raw) return null;

  // میلادی YYYY-MM-DD یا YYYY/MM/DD
  if (/^20\d{2}\/\d{1,2}\/\d{1,2}$/.test(raw) || /^\d{4}-\d{2}-\d{2}$/.test(String(value))) {
    const m = moment(toLatinDigits(value).replace(/\//g, '-'), 'YYYY-MM-DD');
    if (m.isValid()) return m.format('YYYY-MM-DD');
  }

  // جلالی
  const mJ = moment(raw, ['jYYYY/jMM/jDD', 'jYYYY/jM/jD'], true);
  if (mJ.isValid()) return mJ.format('YYYY-MM-DD');

  const mAny = moment(raw, 'jYYYY/jMM/jDD');
  if (mAny.isValid()) return mAny.format('YYYY-MM-DD');

  return null;
}

/** نمایش تاریخ API به‌صورت جلالی برای DatePicker */
export function apiDateToJalali(dateStr) {
  if (!dateStr) return null;
  try {
    const dateOnly = String(dateStr).split('T')[0];
    const m = moment(dateOnly, 'YYYY-MM-DD');
    if (m.isValid()) return m.format('jYYYY/jMM/jDD');
  } catch {
    /* ignore */
  }
  return null;
}

/** HH:mm از dayjs/moment یا رشته */
export function toApiTime(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'string') {
    const s = toLatinDigits(value).trim();
    const m = s.match(/^(\d{1,2}):(\d{2})/);
    if (m) return `${m[1].padStart(2, '0')}:${m[2]}`;
    return s.slice(0, 5);
  }
  if (typeof value.format === 'function') {
    return value.format('HH:mm');
  }
  if (value.$d || value.toDate) {
    const d = value.toDate ? value.toDate() : value.$d;
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
  }
  return null;
}

export const LEAVE_DURATION_OPTIONS = [
  { value: 'hourly', label: 'ساعتی' },
  { value: 'multiple', label: 'چندروزه' },
  { value: 'first_half', label: 'نیم‌روز اول' },
  { value: 'second_half', label: 'نیم‌روز دوم' },
  { value: 'single', label: 'تمام روز' },
];

/** برچسب فارسی مدت مرخصی برای نمایش در جدول و جزئیات */
export function formatLeaveDurationLabel(duration, halfDayType) {
  const key = String(duration || '').toLowerCase().trim().replace(/\s+/g, '_');
  if (key === 'half_day' || key === 'halfday' || key === 'half') {
    if (halfDayType === 'first_half') return 'نیم‌روز اول';
    if (halfDayType === 'second_half') return 'نیم‌روز دوم';
    return 'نیم روز';
  }
  const map = {
    hourly: 'ساعتی',
    multiple: 'چندروزه',
    first_half: 'نیم‌روز اول',
    second_half: 'نیم‌روز دوم',
    single: 'تمام روز',
    full_day: 'تمام روز',
  };
  return map[key] || duration || '--';
}

/**
 * تبدیل duration API به مقدار فرم
 * مقادیر فرم: hourly | multiple | first_half | second_half | single
 */
export function durationApiToFormValue(duration, halfDayType) {
  const key = String(duration || '').toLowerCase().trim().replace(/\s+/g, '_');
  if (['half_day', 'halfday', 'half'].includes(key)) {
    if (halfDayType === 'second_half') return 'second_half';
    return 'first_half';
  }
  if (key === 'hourly') return 'hourly';
  if (key === 'multiple') return 'multiple';
  if (key === 'first_half') return 'first_half';
  if (key === 'second_half') return 'second_half';
  if (key === 'full_day' || key === 'fullday') return 'single';
  // سازگاری با فرم قدیمی
  if (key === 'fullday' || duration === 'fullDay') return 'single';
  if (duration === 'firstHalf') return 'first_half';
  if (duration === 'secondHalf') return 'second_half';
  return 'single';
}

/**
 * ساخت payload ثبت/ویرایش مرخصی از مقادیر فرم
 */
export function buildLeavePayload(values) {
  const duration = values.duration || 'single';
  const payload = {
    user_id: values.member ?? values.user_id,
    leave_type_id: values.leaveType ?? values.leave_type_id,
    status: values.status || 'pending',
    duration,
    reason: values.reason,
  };

  if (duration === 'hourly') {
    payload.leave_date = toApiDate(values.date);
    payload.start_time = toApiTime(values.start_time);
    payload.end_time = toApiTime(values.end_time);
  } else if (duration === 'multiple') {
    let start = values.multiStartDate ?? values.multi_start_date;
    let end = values.multiEndDate ?? values.multi_end_date;
    if ((!start || !end) && Array.isArray(values.dateRange) && values.dateRange.length >= 2) {
      start = values.dateRange[0];
      end = values.dateRange[1];
    }
    payload.multiStartDate = toApiDate(start);
    payload.multiEndDate = toApiDate(end);
  } else {
    // single | first_half | second_half
    payload.leave_date = toApiDate(values.date);
  }

  return payload;
}

/** آیا برای این مدت، فیلد تاریخ تکی لازم است */
export function needsSingleDate(duration) {
  return ['hourly', 'first_half', 'second_half', 'single'].includes(duration);
}

/** آیا فیلد بازه تاریخ لازم است */
export function needsDateRange(duration) {
  return duration === 'multiple';
}

/** آیا فیلد ساعت لازم است */
export function needsTimeRange(duration) {
  return duration === 'hourly';
}

/** استخراج رکورد مرخصی از پاسخ GET */
export function unwrapLeaveRecord(res) {
  const raw = res?.data ?? res;
  if (raw?.type === 'single' && raw?.data) return raw.data;
  if (raw?.id != null) return raw;
  if (raw?.data?.id != null) return raw.data;
  return raw;
}

/** سازگاری با کد قدیمی */
export function durationFormToApi(formDuration) {
  const d = durationApiToFormValue(formDuration);
  return { duration: d, half_day_type: null };
}
